import { useCallback, useMemo } from 'react';

import type { MessageContextData } from '@atlassian/post-office-context/message-context';
import { useMessageInstanceEvent } from '@atlassian/post-office-context/message-lifecycle';

import {
	mapMessageReadStateActionToClickedEvent,
	type MessageReadStateAction,
} from './map-to-clicked-event';

export type PostOfficeMessageReadActions = {
	/** Record that the current message instance was marked as read. */
	markAsRead: () => Promise<void>;
	/** Record that each supplied message instance was marked as read. */
	markMessagesAsRead: (messages: readonly PostOfficeMessageReadTarget[]) => Promise<void>;
	/** Record that the current message instance was marked as unread. */
	markAsUnread: () => Promise<void>;
};

export type PostOfficeMessageReadTarget = Required<
	Pick<
		MessageContextData,
		'messageInstanceId' | 'messageTemplateId' | 'triggerId' | 'messageCreationType'
	>
>;

/**
 * Records read-state analytics for the current Post Office message instance or an explicit list.
 *
 * This hook is intentionally limited to Post Office analytics. The surrounding Post Office
 * providers supply the placement and current-message identifiers; placement-level bulk callers
 * supply the identity of each message instead. Callers own read state and all UI behaviour; do not
 * add UI state here or couple UI updates to these analytics actions.
 *
 * This hook is currently intended only for the Admin Hub notification bell. Teams considering it
 * for another placement must consult Post Office first to determine whether the Notifications
 * Platform (NP) bell is the more appropriate integration.
 *
 * The returned promise resolves when the event has been queued by the message-instance client,
 * not when it has been sent or persisted.
 */
export const usePostOfficeMessageReadActions = (): PostOfficeMessageReadActions => {
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const updateReadState = useCallback(
		(actionType: MessageReadStateAction): Promise<void> =>
			messageInstanceClicked(mapMessageReadStateActionToClickedEvent(actionType)),
		[messageInstanceClicked],
	);

	const markAsRead = useCallback(() => updateReadState('read'), [updateReadState]);
	const markMessagesAsRead = useCallback(
		async (messages: readonly PostOfficeMessageReadTarget[]): Promise<void> => {
			await Promise.all(
				messages.map(({ messageInstanceId, messageTemplateId, triggerId, messageCreationType }) =>
					messageInstanceClicked({
						...mapMessageReadStateActionToClickedEvent('read'),
						messageInstanceId,
						messageTemplateId,
						messageTriggerId: triggerId,
						messageCreationType,
					}),
				),
			);
		},
		[messageInstanceClicked],
	);
	const markAsUnread = useCallback(() => updateReadState('unread'), [updateReadState]);

	return useMemo(
		() => ({ markAsRead, markMessagesAsRead, markAsUnread }),
		[markAsRead, markMessagesAsRead, markAsUnread],
	);
};
